import { useState } from 'react'
import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Gewerbekreis, type KreisStueck } from './Gewerbekreis.js'
import { Fuss } from './Fuss.js'
import { Paketleiste } from './Paketleiste.js'
import { useAuftritt, useBreit, zeigerLicht } from './bewegung.js'
import { START_PACKAGES } from '@raporto/shared'

/**
 * Die Funktionenseite.
 *
 * Aufgebaut wie der Betrieb arbeitet, nicht wie die Software gegliedert ist:
 * oben die Kette Rapport → Offerte → Rechnung, darunter, was sie trägt.
 *
 * Im Kopf steht der Gewerbekreis mit den acht Dingen, um die es geht: eine
 * Karte vorn, die Nachbarn kleiner daneben, darunter Pfeile und Punkte.
 * Bedienbar auf vier Wegen – Karte, Pfeile, Punkte, Pfeiltasten. Die
 * Schaltfläche darunter führt zu dem Feld, das die gewählte Sache
 * ausführlich erklärt.
 *
 * Auf dem Handy klappen die Felder zu. Die Seite war 5012 Pixel lang; das
 * sind elf Bildschirme, durch die niemand scrollt. Zuerst steht da, worum es
 * geht, der Rest kommt auf Tippen. Ab 1041 Pixeln ist alles offen: dort ist
 * der Platz da, und ein Klick, um Vorhandenes zu sehen, wäre Schikane.
 *
 * Alle Klassen tragen ein eigenes Präfix (rp-pg-, rp-fn-). Die Startseite
 * benutzt keine davon; ihre Optik bleibt unberührt.
 *
 * Jede Zeile beschreibt Gebautes. Keine Ankündigung, kein «demnächst».
 */

/* Die Markenfarben als RGB-Tripel, damit sie in rgba(var(--f), …) passen. */
const VIOLETT = '141, 115, 255'
const BLAU = '131, 207, 255'
const TUERKIS = '104, 235, 215'
const PINK = '255, 142, 219'
const GRUEN = '99, 230, 166'
const ROSA = '255, 193, 172'

/*
  Die acht Dinge im Kopf der Seite.

  Auf jeder Karte steht, worum es geht, wozu es gehört und ein Satz dazu. Die
  Schaltfläche darunter führt zu dem Feld, das die Sache ausführlich erklärt.
*/
interface Kopfstueck extends KreisStueck {
  ziel: string
}

const KOPF: Kopfstueck[] = [
  {
    id: 'offerte',
    titel: 'Offerte',
    marke: 'Vorbereiten',
    text: 'Leistungen, Preise und Gültigkeit sauber vorbereitet.',
    ziel: '02'
  },
  {
    id: 'rapport',
    titel: 'Rapport',
    marke: 'Erfassen',
    text: 'Stunden, Material, Fotos und Unterschrift am Einsatz erfassen.',
    ziel: '01'
  },
  {
    id: 'rechnung',
    titel: 'Rechnung',
    marke: 'Abrechnen',
    text: 'Aus erledigten Leistungen wird eine Rechnung mit QR-Zahlteil.',
    ziel: '03'
  },
  {
    id: 'kunden',
    titel: 'Kunden',
    marke: 'Stammdaten',
    text: 'Adressen, Historie und offene Punkte bleiben zusammen.',
    ziel: 'kunden'
  },
  {
    id: 'leistungen',
    titel: 'Leistungen',
    marke: 'Stammdaten',
    text: 'Eigene Positionen einmal pflegen und weiterverwenden.',
    ziel: 'katalog'
  },
  {
    id: 'zahlung',
    titel: 'Zahlung',
    marke: 'Abrechnen',
    text: 'Offene und bezahlte Rechnungen im Überblick behalten.',
    ziel: '03'
  },
  {
    id: 'team',
    titel: 'Team',
    marke: 'Betrieb',
    text: 'Mitarbeitende einladen, Rollen vergeben, Zugriff steuern.',
    ziel: 'team'
  },
  {
    id: 'daten',
    titel: 'Daten',
    marke: 'Betrieb',
    text: 'Alles bleibt exportierbar – als Datei, ohne Frist.',
    ziel: 'daten'
  }
]

interface Stufe {
  nummer: string
  titel: string
  text: string
  punkte: string[]
  bild: 'rapport' | 'offerte' | 'rechnung'
  farbe: string
}

/* Die Kette. Drei Schritte, in der Reihenfolge, in der sie im Betrieb vorkommen. */
const ABLAUF: Stufe[] = [
  {
    nummer: '01',
    titel: 'Rapport auf der Baustelle',
    text: 'Stunden und Material werden dort erfasst, wo sie anfallen – auf dem Handy, auch ohne Empfang.',
    bild: 'rapport',
    farbe: BLAU,
    punkte: ['Läuft offline weiter und gleicht selbständig ab', 'Unterschrift und Fotos am Rapport']
  },
  {
    nummer: '02',
    titel: 'Offerte aus dem Katalog',
    text: 'Positionen aus dem Leistungskatalog, mit Gültigkeitsdatum und Nachfassdatum.',
    bild: 'offerte',
    farbe: VIOLETT,
    punkte: [
      'Wird zur Rechnung, ohne ein zweites Mal zu erfassen',
      'Gewonnen oder verloren wird festgehalten'
    ]
  },
  {
    nummer: '03',
    titel: 'Rechnung mit QR-Zahlteil',
    text: 'Schweizer QR-Rechnung nach Norm, mit QR-IBAN oder Referenz nach SCOR.',
    bild: 'rechnung',
    farbe: TUERKIS,
    punkte: [
      'Rapportzeilen werden übernommen, nicht abgetippt',
      'PDF fertig zum Versand, Zahlung wird festgehalten'
    ]
  }
]

/*
  Der Auszug stammt aus dem echten Startpaket, nicht aus der Fantasie. Eine
  erfundene Beispielzeile auf der Funktionenseite wäre die erste Lüge, die
  beim Einrichten auffliegt.
*/
const KATALOGAUSZUG = (
  START_PACKAGES.find((p) => p.industry === 'boden-parkett')?.items ?? []
).slice(0, 4)

export function Funktionen({
  angemeldet,
  aufAnmelden,
  aufStarten,
  aufStart,
  aufPreise,
  aufPfad,
}: {
  angemeldet: boolean
  aufAnmelden: () => void
  aufStarten: () => void
  aufStart: () => void
  aufPreise: () => void
  aufPfad: (pfad: string) => void
}): JSX.Element {
  const weiter = aufAnmelden
  const breit = useBreit()
  const kapitelA = useAuftritt<HTMLDivElement>()
  const kapitelB = useAuftritt<HTMLDivElement>()
  const ketteAuf = useAuftritt<HTMLDivElement>()
  const grundAuf = useAuftritt<HTMLDivElement>()
  const schlussAuf = useAuftritt<HTMLDivElement>()
  const [offen, setOffen] = useState<string | null>('01')
  const [kopfAktiv, setKopfAktiv] = useState(0)
  const zeigt = (id: string): boolean => breit || offen === id
  const schalten = (id: string): void => setOffen((v) => (v === id ? null : id))

  /*
    Ein Klick im Kopf führt zum Feld, nicht ins Nichts. Auf dem Handy wird
    es dabei aufgeklappt; auf dem Desktop steht es schon offen und muss nur
    noch in den Blick gerückt werden.
  */
  const hinFuehren = (ziel: string): void => {
    setOffen(ziel)
    const feld = document.getElementById(ziel.length === 2 ? `stufe-${ziel}` : `feld-${ziel}`)
    feld?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <div className="rp-pg rp-glasseite">
      <Kopfleiste
        hier="funktionen"
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="So funktioniert’s"
        titel="Vom Baustellenrapport zur"
        hell="QR-Rechnung – ohne Medienbruch."
        vorspann="Was auf der Baustelle erfasst wird, steht auf der Rechnung. Kein zweites Erfassen, keine Zettel dazwischen, keine Tabelle daneben."
      />

      {/*
        Der Kopf der Seite: der Gewerbekreis mit den acht Dingen, um die es
        geht. Die Karte in der Mitte bestimmt, wohin die Schaltfläche darunter
        führt – der Kreis ist die Auswahl und nicht die Verzierung.
      */}
      <section className="rp-pg-kopf rp-pg-kreis">
        <Gewerbekreis
          stuecke={KOPF}
          aktiv={kopfAktiv}
          aufAktiv={setKopfAktiv}
        />
        <div className="rp-pg-aktionen rp-pg-kreisaktion">
          <button
            type="button"
            className="rp-pg-knopf rp-pg-knopf-glas"
            onClick={() => hinFuehren(KOPF[kopfAktiv].ziel)}
          >
            {KOPF[kopfAktiv].titel} im Detail
          </button>
        </div>
      </section>

      <section className="rp-pg-kopf rp-pg-kopf-knoepfe">
        <div className="rp-pg-aktionen">
          <button className="rp-pg-knopf rp-pg-knopf-hell" type="button" onClick={weiter}>
            {angemeldet ? 'Zur App' : 'Kostenlos starten'}
          </button>
          <button className="rp-pg-knopf rp-pg-knopf-glas" type="button" onClick={aufPreise}>
            Preise ansehen
          </button>
        </div>
      </section>

      <section className="rp-pg-abschnitt" id="funktionsablauf">
        <div className="rp-pg-kapitel" ref={kapitelA}>
          <p className="rp-pg-augenbraue">Der Ablauf</p>
          <h2>Drei Schritte, ein Datensatz.</h2>
          <p>
            Was im ersten Schritt erfasst wird, trägt der zweite weiter und der dritte rechnet es
            ab.
          </p>
        </div>

        <div className="rp-pg-gitter rp-fn-kette" ref={ketteAuf} onMouseMove={zeigerLicht}>
          <span className="rp-pg-licht" aria-hidden="true" />
          {ABLAUF.map((s) => (
            <article
              className={`rp-pg-zelle rp-pg-s2 rp-fn-stufe rp-fn-${s.bild}`}
              key={s.nummer}
              style={{ ['--pg-farbe' as string]: s.farbe }}
            >
              <button
                type="button"
                className="rp-pg-schalter"
                aria-expanded={zeigt(s.nummer)}
                aria-controls={`stufe-${s.nummer}`}
                onClick={() => schalten(s.nummer)}
              >
                <span className="rp-fn-zahl">{s.nummer}</span>
                <h3>{s.titel}</h3>
                <span className="rp-pg-pfeil" aria-hidden="true" />
              </button>
              <div
                className="rp-pg-huelle"
                id={`stufe-${s.nummer}`}
                data-auf={zeigt(s.nummer) ? 'ja' : 'nein'}
              >
                <div>
                  <span className="rp-fn-vorschau" aria-hidden="true">
                    <span />
                  </span>
                  <p className="rp-fn-text">{s.text}</p>
                  <ul>
                    {s.punkte.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rp-pg-abschnitt" id="funktionsgrundlage">
        <div className="rp-pg-kapitel" ref={kapitelB}>
          <p className="rp-pg-augenbraue">Was den Ablauf trägt</p>
          <h2>Der Unterbau, den niemand vorführt.</h2>
          <p>
            Vier Dinge ohne Werbewert – an ihnen entscheidet sich, ob eine Software nach einem Jahr
            noch benutzt wird.
          </p>
        </div>

        <div className="rp-pg-gitter" ref={grundAuf} onMouseMove={zeigerLicht}>
          <span className="rp-pg-licht" aria-hidden="true" />
          <article
            className="rp-pg-zelle rp-pg-s4 rp-fn-katalog"
            style={{ ['--pg-farbe' as string]: VIOLETT }}
          >
            <button
              type="button"
              className="rp-pg-schalter"
              aria-expanded={zeigt('katalog')}
              aria-controls="feld-katalog"
              onClick={() => schalten('katalog')}
            >
              <h3>Leistungskatalog</h3>
              <span className="rp-pg-pfeil" aria-hidden="true" />
            </button>
            <div
              className="rp-pg-huelle"
              id="feld-katalog"
              data-auf={zeigt('katalog') ? 'ja' : 'nein'}
            >
              <div>
                <p className="rp-fn-text">
                  Positionen mit Nummer, Einheit, Ansatz und Steuersatz. Beim Einrichten kommt ein
                  Vorschlag für das Gewerbe – änderbar, löschbar, nie wieder überschrieben.
                </p>
                <table className="rp-fn-tabelle">
                  <caption>Auszug Startkatalog «Boden / Parkett»</caption>
                  <tbody>
                    {KATALOGAUSZUG.map((z) => (
                      <tr key={z.code}>
                        <td className="rp-fn-nr">{z.code}</td>
                        <td>{z.name}</td>
                        <td className="rp-fn-einheit">{z.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </article>

          <article
            className="rp-pg-zelle rp-pg-s2"
            style={{ ['--pg-farbe' as string]: BLAU }}
          >
            <button
              type="button"
              className="rp-pg-schalter"
              aria-expanded={zeigt('kunden')}
              aria-controls="feld-kunden"
              onClick={() => schalten('kunden')}
            >
              <h3>Kunden und Baustellen</h3>
              <span className="rp-pg-pfeil" aria-hidden="true" />
            </button>
            <div
              className="rp-pg-huelle"
              id="feld-kunden"
              data-auf={zeigt('kunden') ? 'ja' : 'nein'}
            >
              <div>
                <p className="rp-fn-text">
                  Adressen, Historie, offener Betrag – und die Baustellen, auf die Rapporte und
                  Belege laufen. Bestehende Listen werden mit Vorschau eingelesen, Dubletten
                  erkannt.
                </p>
              </div>
            </div>
          </article>

          <article
            className="rp-pg-zelle rp-pg-s4 rp-fn-breit"
            style={{ ['--pg-farbe' as string]: PINK }}
          >
            <div className="rp-fn-breittext">
              <button
                type="button"
                className="rp-pg-schalter"
                aria-expanded={zeigt('team')}
                aria-controls="feld-team"
                onClick={() => schalten('team')}
              >
                <h3>Team und Zugänge</h3>
                <span className="rp-pg-pfeil" aria-hidden="true" />
              </button>
              <div
                className="rp-pg-huelle"
                id="feld-team"
                data-auf={zeigt('team') ? 'ja' : 'nein'}
              >
                <div>
                  <p className="rp-fn-text">
                    Einladen, Rollen vergeben, deaktivieren. Wer welche Zahlen sieht, ist eine
                    Einstellung und keine Vereinbarung – Rücksetzung per Link, zweiter Faktor für
                    heikle Aktionen.
                  </p>
                </div>
              </div>
            </div>
            <span className="rp-fn-bild rp-fn-team" aria-hidden="true" />
          </article>

          <article
            className="rp-pg-zelle rp-pg-s2"
            style={{ ['--pg-farbe' as string]: TUERKIS }}
          >
            <button
              type="button"
              className="rp-pg-schalter"
              aria-expanded={zeigt('daten')}
              aria-controls="feld-daten"
              onClick={() => schalten('daten')}
            >
              <h3>Daten und Ausstieg</h3>
              <span className="rp-pg-pfeil" aria-hidden="true" />
            </button>
            <div
              className="rp-pg-huelle"
              id="feld-daten"
              data-auf={zeigt('daten') ? 'ja' : 'nein'}
            >
              <div>
                <p className="rp-fn-text">
                  Datenbank in Zürich, je Betrieb strikt getrennt – nicht durch eine Abfrage,
                  sondern durch die Datenbank selbst. Export als Datei, ohne Nachfrage und ohne
                  Frist.
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <Paketleiste aufPreise={aufPreise} />

      <section className="rp-pg-schluss">
        <div className="rp-pg-schlusskasten" ref={schlussAuf}>
          <span className="rp-strahl" aria-hidden="true">
            <span />
          </span>
          <h2>Raporto im Betrieb prüfen.</h2>
          <p>
            Dreissig Tage mit den eigenen Kunden, dem eigenen Katalog und echten Belegen.
            Die Abrechnung beginnt erst nach Ablauf der Testphase.
          </p>
          <div className="rp-pg-aktionen">
            <button className="rp-pg-knopf rp-pg-knopf-hell" type="button" onClick={weiter}>
              {angemeldet ? 'Zur App' : 'Kostenlos starten'}
            </button>
            <button className="rp-pg-knopf rp-pg-knopf-glas" type="button" onClick={() => aufPfad('/branchen')}>
              Startkataloge ansehen
            </button>
          </div>
        </div>
      </section>


      <Fuss aufPfad={aufPfad} />
    </div>
  )
}
